import type { MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance, professionalProfiles } from '@afficheservices/db/schema'
import { sql } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { Map as MapIcon, ArrowRight, Building2, MapPin, Users } from 'lucide-react'
import { VoirAussi, CROSS_LINKS } from '~/components/voir-aussi'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: "Artisans par région — 18 régions de France | AfficheServices" },
  { name: 'description', content: "Trouvez un artisan dans votre région. 18 régions françaises, plus de 100 départements et des milliers d'artisans vérifiés." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/regions' },
  { property: 'og:title', content: 'Artisans par région — 18 régions de France | AfficheServices' },
  { property: 'og:description', content: 'Trouvez un artisan dans votre région. 18 régions françaises.' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/regions' },
]

// Slug for each region (used in /artisans/$region)
const REGION_SLUG: Record<string, string> = {
  'Île-de-France': 'ile-de-france',
  'Auvergne-Rhône-Alpes': 'auvergne-rhone-alpes',
  "Provence-Alpes-Côte d'Azur": 'provence-alpes-cote-d-azur',
  'Nouvelle-Aquitaine': 'nouvelle-aquitaine',
  'Occitanie': 'occitanie',
  'Hauts-de-France': 'hauts-de-france',
  'Grand Est': 'grand-est',
  'Pays de la Loire': 'pays-de-la-loire',
  'Bretagne': 'bretagne',
  'Normandie': 'normandie',
  'Bourgogne-Franche-Comté': 'bourgogne-franche-comte',
  'Centre-Val de Loire': 'centre-val-de-loire',
  'Corse': 'corse',
}

export async function loader() {
  // Aggregate regions from geoFrance
  const regRows = await db
    .select({
      regionName: geoFrance.regionName,
      deptCount: sql<number>`count(DISTINCT ${geoFrance.departmentCode})::int`,
      cityCount: sql<number>`count(DISTINCT ${geoFrance.communeName})::int`,
    })
    .from(geoFrance)
    .where(sql`${geoFrance.departmentCode} NOT LIKE '97%' AND ${geoFrance.departmentCode} NOT LIKE '98%'`)
    .groupBy(geoFrance.regionName)
    .orderBy(geoFrance.regionName)

  // Pro counts per region via department code first 2 digits
  const proCounts = await db
    .select({
      dept: professionalProfiles.departmentCode,
      count: sql<number>`count(*)::int`,
    })
    .from(professionalProfiles)
    .groupBy(professionalProfiles.departmentCode)
  const proByDept = new Map<string, number>()
  for (const r of proCounts) proByDept.set(r.dept, Number(r.count))

  // Map dept → region from geoFrance
  const deptToRegion = new Map<string, string>()
  const deptRows = await db
    .select({
      dept: geoFrance.departmentCode,
      region: geoFrance.regionName,
    })
    .from(geoFrance)
    .groupBy(geoFrance.departmentCode, geoFrance.regionName)
  for (const r of deptRows) deptToRegion.set(r.dept, r.region)

  const proByRegion = new Map<string, number>()
  for (const [dept, count] of proByDept) {
    const reg = deptToRegion.get(dept) ?? ''
    proByRegion.set(reg, (proByRegion.get(reg) ?? 0) + count)
  }

  const regions = regRows.map((r) => ({
    name: r.regionName,
    slug: REGION_SLUG[r.regionName] ?? r.regionName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    deptCount: Number(r.deptCount),
    cityCount: Number(r.cityCount),
    proCount: proByRegion.get(r.regionName) ?? 0,
  })).sort((a, b) => b.proCount - a.proCount)

  const totalRegions = regions.length
  const totalPros = regions.reduce((s, r) => s + r.proCount, 0)

  return { regions, totalRegions, totalPros }
}

export default function RegionsIndexPage() {
  const { regions, totalRegions, totalPros } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Régions', url: 'https://www.afficheservices.fr/regions' },
      ]} />

      <section className="bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-emerald-200">
            <MapIcon className="h-3.5 w-3.5" /> Couverture nationale
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            Artisans par région
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-emerald-200">
            {totalRegions} régions · {totalPros.toLocaleString('fr-FR')}+ artisans vérifiés.
            Explorez par région pour trouver un professionnel de confiance.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {regions.map((r) => (
            <Link
              key={r.slug}
              to={`/artisans/${r.slug}`}
              className="group rounded-2xl border border-border bg-white p-6 shadow-xs transition-all hover:border-primary hover:shadow-md"
            >
              <h2 className="text-lg font-bold text-slate-900 group-hover:text-primary">
                {r.name}
              </h2>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-primary">{r.proCount.toLocaleString('fr-FR')}</p>
                  <p className="text-[10px] text-slate-500">artisans</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{r.deptCount}</p>
                  <p className="text-[10px] text-slate-500">départements</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">{r.cityCount.toLocaleString('fr-FR')}</p>
                  <p className="text-[10px] text-slate-500">villes</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-semibold text-primary">Explorer</span>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary" />
              </div>
            </Link>
          ))}
        </div>

        {/* Cross-links */}
        <section className="mt-12 rounded-2xl border border-border bg-slate-50 p-6">
          <h2 className="text-lg font-bold text-slate-900">Autres façons de chercher</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/departements" className="rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary">
              <Building2 className="mr-1.5 inline h-4 w-4" /> Par département
            </Link>
            <Link to="/toutes-villes" className="rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary">
              <MapPin className="mr-1.5 inline h-4 w-4" /> Par ville
            </Link>
            <Link to="/carte-artisans" className="rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary">
              <MapIcon className="mr-1.5 inline h-4 w-4" /> Carte interactive
            </Link>
            <Link to="/services" className="rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary">
              <Users className="mr-1.5 inline h-4 w-4" /> Par service
            </Link>
          </div>
        </section>

        <VoirAussi links={CROSS_LINKS.services} />

        <section className="mt-10 text-center">
          <Link
            to="/devis-gratuit"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Demander mes devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
